import {
  freeToolHref,
  getFreeToolsByCategory,
  getNavFreeTools,
  type FreeToolIconName,
} from '@/lib/free-tools/registry';

export interface NavLinkItem {
  href: string;
  /**
   * English label. Always present — it is the fallback when no `messageKey` is
   * set, and the readable source when one is.
   */
  label: string;
  /**
   * Catalog key for the translated label, e.g. `ui.nav.pricing`. Present only on
   * links whose destination is a localised surface.
   *
   * Absent by design on links into the blog, comparison, use-case, language and
   * individual free-tool pages: those stay English (I18N_PLAN.md §5), so a
   * translated label would promise a page that does not exist. See the rule in
   * `src/lib/ui-copy.ts`. `src/lib/nav-i18n.test.ts` enforces that every
   * `messageKey` used here actually resolves in the catalog.
   */
  messageKey?: string;
  prefetch?: boolean;
}

/**
 * Root URL segments reserved for static App Router pages.
 * Core pSEO (`app/[slug]`) must never claim these slugs.
 */
export const RESERVED_ROOT_SLUGS = new Set([
  'about',
  'alternatives',
  'blog',
  'changelog',
  'contact',
  'contact-sales',
  'cookie-policy',
  'creators',
  'data-protection',
  'demo',
  'enterprise',
  'extension',
  'faq',
  'features',
  'forgot-password',
  'podcast',
  'pricing',
  'privacy',
  'reset-password',
  'sales',
  'sign-in',
  'sign-up',
  'stats',
  'status',
  'teams',
  'terms',
  'tools',
  'use-cases',
]);

/** Slugs that have dedicated hub index pages (fallback when `[slug]` wins route matching). */
// SAFETY: The surrounding typed contract establishes the asserted representation.
export const MARKETING_HUB_SLUGS = ['tools', 'alternatives', 'podcast'] as const;

export type MarketingHubSlug = (typeof MARKETING_HUB_SLUGS)[number];

export function isMarketingHubSlug(slug: string): slug is MarketingHubSlug {
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  return (MARKETING_HUB_SLUGS as readonly string[]).includes(slug);
}

/**
 * Free-tool pages, derived from the registry.
 *
 * @deprecated for new code — this projection drops description/category/icon.
 * Prefer `getNavFreeTools()` / `getFreeToolNavGroups()` so the surface can render
 * grouped, described links. Kept because the footer and the hub still consume it.
 */
export const FREE_TOOL_APP_LINKS: NavLinkItem[] = getNavFreeTools().map((tool) => ({
  href: freeToolHref(tool.slug),
  label: tool.label,
  prefetch: false,
}));

/** Grouped nav data for the Free Tools mega menu. Serializable: `iconName` is a string. */
export interface NavMenuGroup {
  heading: string;
  items: Array<NavLinkItem & { description?: string; iconName?: FreeToolIconName }>;
}

export function getFreeToolNavGroups(): NavMenuGroup[] {
  return getFreeToolsByCategory().map((group) => ({
    heading: group.label,
    items: group.tools.map((tool) => ({
      href: freeToolHref(tool.slug),
      label: tool.label,
      description: tool.description,
      iconName: tool.iconName,
      prefetch: false,
    })),
  }));
}

/** P0 comparison / alternatives pages for nav dropdown. */
export const COMPARE_NAV = {
  hub: {
    href: '/alternatives',
    label: 'View all comparisons →',
    messageKey: 'ui.nav.compareHub',
  } satisfies NavLinkItem,
  featured: [
    {
      href: '/alternatives/best-otter-ai-alternatives',
      label: 'Best Otter.ai Alternatives',
      prefetch: false,
    },
    { href: '/alternatives/wisprs-vs-otter-ai', label: 'Wisprs vs Otter.ai', prefetch: false },
    { href: '/alternatives/wisprs-vs-descript', label: 'Wisprs vs Descript', prefetch: false },
    {
      href: '/alternatives/best-transcription-software',
      label: 'Best Transcription Software',
      prefetch: false,
    },
    {
      href: '/alternatives/descript-alternatives',
      label: 'Descript Alternatives',
      prefetch: false,
    },
  ] satisfies NavLinkItem[],
};

export const FOOTER_HUB_LINKS: NavLinkItem[] = [
  { href: '/tools', label: 'Free tools' },
  { href: '/alternatives', label: 'Comparisons' },
  { href: '/use-cases', label: 'Use cases' },
  { href: '/podcast', label: 'Podcast transcription' },
];

/** Primary nav links shared by desktop header and mobile drawer. */
export const PRIMARY_NAV_LINKS: NavLinkItem[] = [
  { href: '/features', label: 'Features', messageKey: 'ui.nav.features', prefetch: true },
  { href: '/use-cases', label: 'Use Cases', messageKey: 'ui.nav.useCases', prefetch: true },
  { href: '/pricing', label: 'Pricing', messageKey: 'ui.nav.pricing', prefetch: true },
  { href: '/faq', label: 'FAQs', messageKey: 'ui.nav.faq', prefetch: false },
  { href: '/blog', label: 'Blog', messageKey: 'ui.nav.blog', prefetch: false },
];

/**
 * Mobile drawer entries. Tools are grouped into collapsible sections rather than
 * inlined flat: 24 tool links in a `max-w-sm` drawer is a very long scroll with
 * no scent. Descriptions are omitted on mobile, where they double the scroll for
 * no conversion gain.
 */
export type MobileNavEntry =
  | { type: 'link'; item: NavLinkItem }
  | { type: 'section'; heading: string; items: NavLinkItem[] };

export function getMobileNavEntries(): MobileNavEntry[] {
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  return [
    ...PRIMARY_NAV_LINKS.map((item) => ({ type: 'link' as const, item })),
    {
      type: 'link' as const,
      item: {
        href: COMPARE_NAV.hub.href,
        label: 'Compare',
        messageKey: 'ui.nav.compare',
        prefetch: false,
      },
    },
    ...getFreeToolNavGroups().map((group) => ({
      type: 'section' as const,
      heading: group.heading,
      items: group.items.map(({ href, label, messageKey, prefetch }) => ({
        href,
        label,
        messageKey,
        prefetch,
      })),
    })),
    {
      type: 'link' as const,
      item: {
        href: '/tools',
        label: 'Browse all free tools →',
        messageKey: 'ui.nav.freeToolsHub',
        prefetch: false,
      },
    },
  ];
}

/**
 * Flat projection of {@link getMobileNavEntries}, kept for any consumer that
 * cannot render sections.
 */
export function getMobileNavLinks(): NavLinkItem[] {
  return getMobileNavEntries().flatMap((entry) =>
    entry.type === 'link' ? [entry.item] : entry.items
  );
}
